import { compactNumber } from "@/lib/formatNumber";
import { avatarSrc } from "@/lib/defaults";
import { renderMentions, useMentionAutocomplete, MentionSuggestions } from "@/components/MentionText";
import { ExpandableText } from "@/components/ExpandableText";
import { renderRichText } from "@/components/RichText";
import { PostRichEditor } from "@/components/composer/PostRichEditor";
import { feedTextMaxChars } from "@/lib/textClamp";
import { AttachLiveToChurchDialog, loadMyChurches } from "@/components/AttachLiveToChurchDialog";
import { AudienceOptions } from "@/components/AudienceOptions";
import { audienceOf, writeDefaultAudience } from "@/lib/postAudience";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { usePostList, videoNeighbours } from "@/lib/postList";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { requireLogin } from "@/lib/guestGate";
import { useApp } from "@/context/AppContext";
import { localizeReference } from "@/lib/bibleBooks";
import { hasAnyRole } from "@/lib/roles";
import { ReactionBar } from "@/components/ReactionBar";
import { SharedPostCard } from "@/components/SharedPostCard";
import { GroupSharesLine } from "@/components/GroupSharesLine";
import { PostPhotos } from "@/components/PhotoGallery";
import { FeedVideo } from "@/components/FeedVideo";
import { usePostPatch, emitPostPatch } from "@/lib/postSync";
import { useIsMobile } from "@/lib/useIsMobile";
import { mobileViewerKind, postBeforeAfter, postHasVideoAlbum } from "@/lib/postMedia";
import { AlbumMosaic } from "@/components/post/AlbumMosaic";
import { BeforeAfterBlock } from "@/components/post/BeforeAfter";
import { PostImageMusic } from "@/components/PostImageMusic";
import { ReelFeedPreview } from "@/components/ReelPreview";
import { PromoteModal } from "@/components/PromoteModal";
import { CommentModal } from "@/components/CommentModal";
import { CommentThread } from "@/components/CommentThread";
import { PostAudioPlayer } from "@/components/PostAudioPlayer";
import { SaveCollectionDialog } from "@/components/SaveCollectionDialog";
import { ShareTestimony } from "@/components/ShareTestimony";
import { AudioSharePoster } from "@/components/AudioSharePoster";
import { postShareLocked } from "@/lib/privacy";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody,
} from "@/components/ui/dialog";
import { MoreHorizontal, Megaphone, Pencil, Trash2, EyeOff, UserX, Flag, Check, X, Sparkles, Bookmark, Radio, TrendingUp, HeartHandshake, Star, Image as ImageIcon, ShieldAlert, Languages, Church, Repeat2, Download } from "lucide-react";
import { saveAllToGallery } from "@/lib/saveMedia";
import { toastSaveResult } from "@/components/LightboxMenu";
import { isNudgeMuted, muteNudgeDays } from "@/lib/promoteNudge";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { Ban } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { AdCtaFooter } from "@/components/AdCtaFooter";
import { resolveMediaUrl } from "@/components/FileUpload";
import { gradientCss, bgStyle } from "@/lib/gradients";
import { AutoFitText } from "@/components/AutoFitText";
import { BgTextFrame, BG_TEXT_PAD, BG_TEXT_SHADOW, BG_TEXT_MAX_PX } from "@/components/BgTextFrame";
import { parseVideoEmbed, parseFirstUrl } from "@/lib/embeds";
import { stripEmbedUrl } from "@/lib/linkText";
import { useLinkPreview } from "@/components/LinkCard";
import { EmbedPlayer } from "@/components/EmbedPlayer";
import { DoubleTapHeart } from "@/components/DoubleTapHeart";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { timeAgo } from "@/lib/timeAgo";

export const PostCard = ({ post, onReactionChange, onDeleted, onHidden, hideMedia = false, defaultShowComments = false, canModerate = false, allowNudge = false, focusTarget = null, focusCommentId = null, theaterMode = false, preview = false }) => {
  const { user, t, lang, config } = useApp();
  const nav = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  // Fresh server data (feed refresh) or any other card / viewer of this post → stay in sync.
  useEffect(() => { setCommentCount(post.comment_count || 0); }, [post.id, post.comment_count]);
  usePostPatch(post.id, (patch) => { if (typeof patch.comment_delta === "number") setCommentCount((c) => Math.max(0, c + patch.comment_delta)); });
  // Deep-link focus (from a notification): gently animate the target to signal it.
  const reactionAreaRef = useRef(null);
  const commentsRef = useRef(null);
  const editRef = useRef(null);
  const imgMusicRef = useRef(null);
  const focusDoneRef = useRef(false);
  const [pulseReactions, setPulseReactions] = useState(false);
  // Lot 601 — double-tap sur une photo / vidéo → cœur animé + « J'adore » (via la ReactionBar, jamais de retrait).
  const [quickReactTick, setQuickReactTick] = useState(0);
  const quickLove = () => {
    if (!user) { requireLogin("react"); return false; }
    setQuickReactTick(Date.now());
    return true;
  };

  const [content, setContent] = useState(post.content || "");
  // Lot 649 — audience de la publication (Public / Abonnés / Abonnés mutuels / Suivis / Moi), modifiable par l'auteur.
  const [audience, setAudience] = useState(post.visibility && post.visibility !== "group" ? post.visibility : "public");
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceBusy, setAudienceBusy] = useState(false);
  const audienceEditable = !post.group_id && !post.as_church_id && !post.is_repost;
  const changeAudience = async (v) => {
    if (v === audience) { setAudienceOpen(false); return; }
    setAudienceBusy(true);
    try {
      await api.patch(`/posts/${post.id}`, { visibility: v });
      setAudience(v); post.visibility = v; writeDefaultAudience(v);
      setAudienceOpen(false);
      toast.success(t("audience.changed", "Audience modifiée : {label}").replace("{label}", t(audienceOf(v).tkey, audienceOf(v).label)));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setAudienceBusy(false); }
  };
  const viewerLang = (user?.language || lang || "fr").toLowerCase().slice(0, 2);
  const _mlFeatures = config?.settings?.features || {};
  const discoveryOn = _mlFeatures.multilingual_discovery !== false;
  const showLangUi = _mlFeatures.language_filter_ui !== false;
  const translationOn = _mlFeatures.post_translation !== false;
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  const canTranslate = translationOn && !!content && !post.content_auto && !post.verse && ["fr", "en", "es", "pt"].includes(post.language) && post.language !== viewerLang;
  const doTranslate = async () => {
    if (translated) { setShowTranslated((v) => !v); return; }
    setTranslating(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/translate`, { target: viewerLang });
      setTranslated(data.translated || content);
      setShowTranslated(true);
    } catch { toast.error(t("common.error", "Une erreur est survenue")); }
    finally { setTranslating(false); }
  };
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || "");
  // Mentions du post (mises à jour après une modification) + autocomplétion @/# pendant l'édition.
  const [mentions, setMentions] = useState(post.mentions || []);
  const [editMentions, setEditMentions] = useState([]);
  const editAc = useMentionAutocomplete({ text: editing ? editText : "", setText: setEditText, onPicked: (u) => setEditMentions((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, u].slice(-20))) });
  const startEdit = () => { setEditText(post.content_auto ? "" : content); setEditMentions(mentions.filter((m) => m && m.id && m.name)); setEditing(true); };
  // Carte de lien (vidéo YouTube/Facebook ou article) affichée sous le texte → l'URL brute n'est pas répétée dans le texte.
  // Lot 667 : sans carte enregistrée (anciennes publications, lien Facebook/article collé), l'aperçu est récupéré à
  // l'affichage — sauf si l'auteur a retiré la carte en publiant (embed_dismissed).
  const storedEmbed = (post.embed && (post.embed.src || post.embed.kind === "article")) ? post.embed : parseVideoEmbed(content);
  const lazyUrl = (!storedEmbed && !post.embed_dismissed && !post.images?.length && !post.video_url && !post.verse) ? parseFirstUrl(content) : null;
  const lazyEmbed = useLinkPreview(lazyUrl);
  const embed = storedEmbed || (lazyEmbed?.kind === "article" ? lazyEmbed : null);
  const embedShown = !hideMedia && !post.images?.length && !post.video_url && !!embed;
  const rawContent = post.verse ? (post.verse[`note_${lang}`] || post.verse.note_fr || content) : (showTranslated && translated ? translated : content);
  const displayContent = embedShown ? stripEmbedUrl(rawContent, embed) : rawContent;
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoted, setPromoted] = useState(!!post.is_promoted || !!post.is_ad);
  const [confirmDel, setConfirmDel] = useState(false);
  const [saved, setSaved] = useState(!!post.is_saved);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  // PC theater ‹ › : the modal can show ANOTHER video of the same feed than this card's post.
  const postList = usePostList();
  const [theaterPost, setTheaterPost] = useState(null);
  const shownPost = theaterPost || post;
  const wantNextRef = useRef(false);
  const extraPagesRef = useRef(0);
  const [navLoading, setNavLoading] = useState(false);
  const neighbours = useMemo(
    () => (postList && commentModalOpen ? videoNeighbours(postList.posts, shownPost.id) : null),
    [postList, commentModalOpen, shownPost.id],
  );
  // « next » asked at the end of the loaded feed: keep paging (max 5 pages) until a video shows up.
  useEffect(() => {
    if (!wantNextRef.current || !neighbours || !postList) return;
    if (neighbours.next) { wantNextRef.current = false; setNavLoading(false); setTheaterPost(neighbours.next); return; }
    if (postList.loadingMore) return;
    if (postList.hasMore && extraPagesRef.current < 5) { extraPagesRef.current += 1; postList.loadMore(); return; }
    wantNextRef.current = false; setNavLoading(false);
  }, [neighbours, postList]);
  const videoNav = neighbours?.known && postList ? {
    hasPrev: !!neighbours.prev,
    hasNext: !!neighbours.next || !!postList.hasMore,
    loading: navLoading,
    prev: () => { if (neighbours.prev) setTheaterPost(neighbours.prev); },
    next: () => {
      if (neighbours.next) { setTheaterPost(neighbours.next); return; }
      if (postList.hasMore) { wantNextRef.current = true; extraPagesRef.current = 0; setNavLoading(true); postList.loadMore(); }
    },
  } : null;
  const onCommentModalChange = (o) => { setCommentModalOpen(o); if (!o) { setTheaterPost(null); wantNextRef.current = false; setNavLoading(false); } };
  const [featured, setFeatured] = useState(!!post.testimony_featured);
  const [shareOpen, setShareOpen] = useState(false);
  const [audioShareOpen, setAudioShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reporting, setReporting] = useState(false);

  const author = post.author || {};
  // Published in the name of a church → the author links to the CHURCH page, not the preacher.
  const authorLink = author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`;
  const isOwner = post.is_owner || (user && user.id === author.id);
  const shareLocked = postShareLocked(post, user); // profil privé : « Partager en image » réservé à l'auteur
  // « Télécharger » depuis le fil (lot 577) : tous les médias de l'album, sinon les photos, sinon la vidéo (mp4).
  const downloadItems = useMemo(() => {
    if (Array.isArray(post.album) && post.album.length > 1) return post.album.filter((m) => m.url).map((m) => ({ url: resolveMediaUrl(m.url), kind: m.type === "video" ? "video" : "image" }));
    if (post.images?.length) return post.images.map((u) => ({ url: resolveMediaUrl(u), kind: "image" }));
    if (post.video_url) return [{ url: resolveMediaUrl(post.video_url), kind: "video" }];
    return [];
  }, [post.album, post.images, post.video_url]);
  const [dlBusy, setDlBusy] = useState(false);
  const doDownload = async () => {
    if (dlBusy) return;
    setDlBusy(true);
    const n = downloadItems.length;
    const tid = n > 1 ? toast.loading(t("viewer.saving_n", "Enregistrement de {n} médias…").replace("{n}", String(n))) : null;
    try {
      const { saved, total, how } = await saveAllToGallery(downloadItems);
      if (tid) toast.dismiss(tid);
      if (saved === total) toastSaveResult(t, how, total);
      else if (how === "denied" || how === "error" || saved === 0) toastSaveResult(t, how === "cancelled" ? null : how || "error");
      else toast.warning(t("viewer.saved_partial", "{saved} média(s) sur {total} enregistré(s)").replace("{saved}", String(saved)).replace("{total}", String(total)));
    } finally { setDlBusy(false); }
  };
  const downloadLabel = downloadItems.length > 1
    ? t("post.download_n", "Télécharger les {n} médias").replace("{n}", String(downloadItems.length))
    : downloadItems[0]?.kind === "video" ? t("viewer.download_video", "Télécharger la vidéo") : t("post.download_photo", "Télécharger la photo");
  const canFeature = post.post_type === "testimony" && hasAnyRole(user, ["super_admin", "moderator"]);
  const toggleFeature = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/feature-testimony`);
      setFeatured(data.featured);
      toast.success(data.featured ? t("testimony.featured_on", "Mis en une du fil ⭐") : t("testimony.featured_off", "Retiré de la une"));
    } catch { toast.error(t("common.error", "Erreur")); }
  };
  const nudgeCfg = config?.settings?.promote_nudge || {};
  const reactionTotal = post.reaction_total || 0;
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const nudgeRecency = nudgeCfg.recency_days ?? 14;
  const nudgeAgeOk = !(nudgeRecency > 0) || (post.created_at && (Date.now() - new Date(post.created_at).getTime()) <= nudgeRecency * 86400000);
  const showPromoteNudge = allowNudge && !nudgeDismissed && !isNudgeMuted()
    && isOwner && !promoted && !post.is_ad && nudgeCfg.enabled !== false && nudgeAgeOk
    && (reactionTotal >= (nudgeCfg.min_reactions ?? 10) || commentCount >= (nudgeCfg.min_comments ?? 5));
  const dismissNudge = () => {
    setNudgeDismissed(true);
    muteNudgeDays(nudgeCfg.cooldown_days ?? 7);
  };
  const showRole = author.role === "preacher" || author.role === "artist" || author.role === "influencer";
  const showVerified = author.is_verified && showRole;

  // Feed: clicking "Comment" opens the Facebook-style pop-up. The post-detail /
  // theater page shows the full inline comment thread (defaultShowComments).
  // Téléphone (façon Instagram) : publication avec photo/vidéo/carte texte → visionneuse plein écran,
  // feuille de commentaires déjà ouverte ; texte simple → fiche claire « Publication de … ».
  const isMobile = useIsMobile();
  const handleCommentClick = () => {
    if (defaultShowComments) { setShowComments(true); return; }
    if (isMobile && mobileViewerKind(post)) { nav(`/post/${post.id}?focus=comments`); return; }
    setCommentModalOpen(true);
  };

  useEffect(() => {
    if (defaultShowComments) setShowComments(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When arriving from a notification, scroll to + briefly animate the concerned
  // comment (or the reactions bar) so the user sees exactly what it's about.
  useEffect(() => {
    if (!focusTarget || focusDoneRef.current) return;
    const timer = setTimeout(() => {
      focusDoneRef.current = true; // mark done only after acting (survives StrictMode double-mount)
      if (focusTarget === "reactions") {
        reactionAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setPulseReactions(true);
        setTimeout(() => setPulseReactions(false), 2200);
      } else if (focusTarget === "comments" && !focusCommentId) {
        // A specific comment (focusCommentId) is scrolled + pulsed by CommentThread itself.
        commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, focusCommentId]);

  const saveEdit = async () => {
    try {
      const used = editMentions.filter((m) => editText.includes(`@${m.name}`));
      const { data } = await api.patch(`/posts/${post.id}`, { content: editText, mentions: used });
      setContent(editText);
      setMentions(data?.post?.mentions || used);
      setEditing(false);
      toast.success(t("post.edited_toast", "Publication modifiée"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  // Rediffusion d'un direct : le prédicateur qui gère une église peut la rattacher à la page de l'église
  // depuis le menu ⋯ (visible seulement s'il gère ≥ 1 église).
  const isReplayPost = !!(post.is_replay || post.source_live_id);
  const [attachOpen, setAttachOpen] = useState(false);
  const [canAttachChurch, setCanAttachChurch] = useState(false);
  const [attachedChurchId, setAttachedChurchId] = useState(post.church_id || null);
  const onMenuOpen = (o) => { if (o && isOwner && isReplayPost && user) loadMyChurches().then((chs) => setCanAttachChurch(chs.length > 0)); };

  const doDelete = async () => {
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success(t("post.deleted_toast", "Publication supprimée"));
      onDeleted?.(post.id);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const doHide = async () => {
    try {
      await api.post(`/posts/${post.id}/hide`);
      toast.success(t("post.hidden_toast", "Publication masquée"));
      onHidden?.(post.id);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const doModHide = async () => {
    try {
      await api.post(`/admin/content/post/${post.id}/action`, { action: "hide" });
      toast.success(t("post.hidden_toast", "Publication masquée"));
      onHidden?.(post.id);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const doUnfollow = async () => {
    try {
      await api.delete(`/users/${author.id}/follow`);
      toast.success(t("post.unfollowed_toast", "Vous ne suivez plus {name}").replace("{name}", author.name));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const REPORT_REASONS = [
    { key: "spam", label: t("report.reason_spam", "Spam ou arnaque") },
    { key: "hate", label: t("report.reason_hate", "Harcèlement ou discours haineux") },
    { key: "sexual", label: t("report.reason_sexual", "Nudité ou contenu sexuel") },
    { key: "violence", label: t("report.reason_violence", "Violence ou contenu choquant") },
    { key: "misinfo", label: t("report.reason_misinfo", "Fausse information") },
    { key: "other", label: t("report.reason_other", "Autre contenu inapproprié") },
  ];

  const doReport = async (reason, category) => {
    if (reporting) return;
    setReporting(true);
    try {
      await api.post(`/posts/${post.id}/report`, { reason, category });
      toast.success(
        category === "child_safety"
          ? t("report.csae_toast", "Signalement urgent transmis à notre équipe de sécurité. Merci de protéger notre communauté.")
          : t("post.reported_toast", "Merci, la publication a été signalée aux modérateurs")
      );
      setReportOpen(false);
      onHidden?.(post.id);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setReporting(false); }
  };

  // Republication : en-tête « {name} a republié » (reposted_by = auteur de la republication) et
  // « Annuler la republication » dans le menu ⋯ quand c'est la mienne.
  const repostedBy = post.reposted_by || null;
  const [myRepost, setMyRepost] = useState(!!post.my_repost);
  useEffect(() => { setMyRepost(!!post.my_repost); }, [post.id, post.my_repost]);
  usePostPatch(post.id, (patch) => { if ("my_repost" in patch) setMyRepost(!!patch.my_repost); });
  const undoRepost = async () => {
    try {
      const { data } = await api.delete(`/posts/${post.id}/repost`);
      emitPostPatch(post.id, { my_repost: false, repost_count: data.repost_count || 0 });
      toast.success(t("post.unreposted_toast", "Republication annulée"));
      if (repostedBy && user && repostedBy.id === user.id) onDeleted?.(post.id, post.repost_id);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const toggleSave = async () => {
    if (!user) { requireLogin("save"); return; }
    try {
      if (saved) {
        await api.delete(`/posts/${post.id}/save`);
        setSaved(false);
        toast.success(t("post.unsaved", "Retiré des enregistrements"));
      } else {
        await api.post(`/posts/${post.id}/save`);
        setSaved(true);
        toast.success(t("post.saved", "Publication enregistrée"), {
          action: { label: t("post.arrange", "Ranger"), onClick: () => setCollectionOpen(true) },
        });
      }
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  return (
    <article className={`yalola-card p-5 float-up ${preview ? "pointer-events-none select-none" : ""}`} data-testid={`post-${post.id}`} data-repost-id={post.repost_id || undefined} data-preview={preview ? "1" : undefined}>
      {/* lot 779 — `preview` : carte figée (aperçu avant publication) — pas de menu ⋯, aucune interaction */}
      {repostedBy && (
        <Link to={`/profile/${repostedBy.id}`} className="flex items-center gap-1.5 text-xs font-semibold mb-2 hover:underline" style={{ color: "var(--yalola-text-muted)" }} data-testid={`post-reposted-by-${post.id}`}>
          <Repeat2 size={14} />
          <span className="truncate">
            {user && repostedBy.id === user.id
              ? t("post.you_reposted", "Tu as republié")
              : t("post.reposted_by", "{name} a republié").replace("{name}", repostedBy.name || "")}
          </span>
          {post.repost_created_at && <span className="font-normal shrink-0">· {timeAgo(post.repost_created_at, t)}</span>}
        </Link>
      )}
      {promoted && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`post-sponsored-${post.id}`}>
          <Megaphone size={12} /> {t("post.sponsored", "Sponsorisé")}
        </div>
      )}
      <header className="flex items-start gap-3">
        <Link to={authorLink}>
          <Avatar className="h-11 w-11">
            <AvatarImage src={avatarSrc(author)} />
            <AvatarFallback>{author.name?.[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <Link to={authorLink} className="font-semibold text-sm hover:underline truncate max-w-full min-w-0" data-testid={`post-author-${post.id}`}>{author.name}</Link>
            {showVerified && <VerifiedBadge user={author} size={14} />}
            {config?.settings?.ai?.show_ai_badge && author?.is_bot && (
              <span className="chip text-[10px] inline-flex items-center gap-0.5" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`post-ai-badge-${post.id}`}>
                <Sparkles size={10} /> IA
              </span>
            )}
            {post.is_replay && (
              <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`post-replay-badge-${post.id}`}>
                <Radio size={10} /> {t("post.replay_badge", "Rediffusion")}
              </span>
            )}
            {post.post_type === "testimony" && (
              <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "var(--yalola-secondary-soft)", color: "var(--yalola-secondary)" }} data-testid={`post-testimony-badge-${post.id}`}>
                <HeartHandshake size={10} /> {t("testimony.badge", "Témoignage")}
                {post.testimony_category && post.testimony_category !== "other" && (
                  <span className="opacity-80">· {t(`testimony.cat_${post.testimony_category}`, post.testimony_category)}</span>
                )}
              </span>
            )}
            {discoveryOn && showLangUi && post.language && ["fr","en","es","pt"].includes(post.language) && (
              <span className="chip text-[10px] uppercase" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid={`post-lang-${post.id}`}>{post.language}</span>
            )}
          </div>
          <div className="text-xs flex items-center gap-1 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
            {timeAgo(post.created_at, t)}{post.edited_at ? ` · ${t("post.edited", "modifié")}` : ""}
            {audienceEditable && !post.group && (() => { const a = audienceOf(audience); return (
              <span className="inline-flex items-center" style={a.accent ? { color: a.accent } : undefined} title={`${t(a.tkey, a.label)} · ${t(a.dkey, a.desc)}`} aria-label={t(a.tkey, a.label)} data-testid={`post-audience-icon-${post.id}`} data-audience={a.key}>
                · <a.icon size={12} className="ml-1" />
              </span>
            ); })()}
            {post.is_replay && <span data-testid={`post-replay-views-${post.id}`}> · {compactNumber(post.replay_views || 0)} {t("post.views", "vue(s)")}</span>}
          </div>
          <GroupSharesLine post={post} />
        </div>

        {!preview && (
        <DropdownMenu onOpenChange={onMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button className="opacity-60 hover:opacity-100 outline-none" data-testid={`post-menu-${post.id}`}><MoreHorizontal size={18} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid={`post-menu-content-${post.id}`}>
            {user && (
              <>
                <DropdownMenuItem onClick={toggleSave} data-testid={`post-save-${post.id}`}>
                  <Bookmark size={15} className="mr-2" fill={saved ? "currentColor" : "none"} />
                  {saved ? t("post.unsave_action", "Retirer des enregistrements") : t("post.save_action", "Enregistrer la publication")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {user && myRepost && (
              <>
                <DropdownMenuItem onClick={undoRepost} data-testid={`post-unrepost-${post.id}`}>
                  <Repeat2 size={15} className="mr-2" /> {t("post.unrepost", "Annuler la republication")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {downloadItems.length > 0 && !shareLocked && (
              <DropdownMenuItem onClick={doDownload} disabled={dlBusy} data-testid={`post-download-${post.id}`} data-count={downloadItems.length}>
                <Download size={15} className="mr-2" /> {downloadLabel}
              </DropdownMenuItem>
            )}
            {post.post_type === "testimony" && !shareLocked && (
              <DropdownMenuItem onClick={() => setShareOpen(true)} data-testid={`post-share-image-${post.id}`}>
                <ImageIcon size={15} className="mr-2" /> {t("share.as_image", "Partager en image")}
              </DropdownMenuItem>
            )}
            {post.audio_url && post.post_type !== "testimony" && !shareLocked && (
              <DropdownMenuItem onClick={() => setAudioShareOpen(true)} data-testid={`post-share-audio-${post.id}`}>
                <ImageIcon size={15} className="mr-2" /> {t("share.as_image", "Partager en image")}
              </DropdownMenuItem>
            )}
            {canFeature && (
              <>
                <DropdownMenuItem onClick={toggleFeature} data-testid={`post-feature-${post.id}`}>
                  <Star size={15} className="mr-2" fill={featured ? "currentColor" : "none"} />
                  {featured ? t("testimony.unfeature_action", "Retirer de la une") : t("testimony.feature_action", "Mettre en une du fil")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {isOwner ? (
              <>
                <DropdownMenuItem onClick={startEdit} data-testid={`post-edit-${post.id}`}>
                  <Pencil size={15} className="mr-2" /> {t("post.edit_text", "Modifier le texte")}
                </DropdownMenuItem>
                {audienceEditable && (
                  <DropdownMenuItem onClick={() => setAudienceOpen(true)} data-testid={`post-audience-edit-${post.id}`}>
                    {React.createElement(audienceOf(audience).icon, { size: 15, className: "mr-2" })} {t("audience.edit", "Modifier l'audience")}
                  </DropdownMenuItem>
                )}
                {isReplayPost && canAttachChurch && (
                  <DropdownMenuItem onClick={() => setAttachOpen(true)} data-testid={`post-attach-church-${post.id}`}>
                    <Church size={15} className="mr-2" /> {t("live.attach_church", "Rattacher à l'église")}
                  </DropdownMenuItem>
                )}
                {!promoted && (
                  <DropdownMenuItem onClick={() => setPromoteOpen(true)} data-testid={`post-promote-${post.id}`}>
                    <Megaphone size={15} className="mr-2" /> {t("post.promote", "Promouvoir")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConfirmDel(true)} className="text-red-600 focus:text-red-600" data-testid={`post-delete-${post.id}`}>
                  <Trash2 size={15} className="mr-2" /> {t("common.delete", "Supprimer")}
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem onClick={doHide} data-testid={`post-hide-${post.id}`}>
                  <EyeOff size={15} className="mr-2" /> {t("post.hide", "Masquer cette publication")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doUnfollow} data-testid={`post-unfollow-${post.id}`}>
                  <UserX size={15} className="mr-2" /> {t("post.unfollow", "Ne plus suivre {name}").replace("{name}", author.name)}
                </DropdownMenuItem>
                {!author.is_protected && (
                  <DropdownMenuItem onClick={() => setBlockOpen(true)} className="text-red-600 focus:text-red-600" data-testid={`post-block-${post.id}`}>
                    <Ban size={15} className="mr-2" /> {t("block.action", "Bloquer {name}").replace("{name}", author.name)}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {canModerate && (
                  <DropdownMenuItem onClick={doModHide} data-testid={`post-moderate-hide-${post.id}`}>
                    <EyeOff size={15} className="mr-2" /> {t("post.mod_hide", "Masquer (modération)")}
                  </DropdownMenuItem>
                )}
                {canModerate && (
                  <DropdownMenuItem onClick={() => setConfirmDel(true)} className="text-red-600 focus:text-red-600" data-testid={`post-moderate-delete-${post.id}`}>
                    <Trash2 size={15} className="mr-2" /> {t("post.mod_delete", "Supprimer (modération)")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setReportOpen(true)} className="text-red-600 focus:text-red-600" data-testid={`post-report-${post.id}`}>
                  <Flag size={15} className="mr-2" /> {t("post.report", "Signaler")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </header>

      {editing ? (
        <div className="mt-3">
          <PostRichEditor
            editorRef={editRef}
            autoFocus
            value={editText}
            onChange={setEditText}
            className="w-full rounded-xl border p-3 text-[15px] min-h-[4.5rem]"
            style={{ borderColor: "var(--yalola-border)" }}
            toolbarClassName="mb-1.5 w-max"
            testId={`post-edit-input-${post.id}`}
            toolbarTestId={`post-edit-toolbar-${post.id}`}
          />
          {/* @mention / #hashtag : propositions sous le champ pendant la modification */}
          <MentionSuggestions ac={editAc} className="mt-1" testId={`post-edit-suggestions-${post.id}`} t={t} />
          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid={`post-edit-cancel-${post.id}`}><X size={15} className="mr-1" /> {t("common.cancel", "Annuler")}</Button>
            <Button size="sm" className="pill-primary" onClick={saveEdit} data-testid={`post-edit-save-${post.id}`}><Check size={15} className="mr-1" /> {t("common.save", "Enregistrer")}</Button>
          </div>
        </div>
      ) : displayContent && post.bg && bgStyle(post.bg) && !post.images?.length && !post.video_url && !post.audio_url ? (
        <BgTextFrame bg={post.bg} className={`mt-3 ${isMobile ? "cursor-pointer" : ""}`} onClick={isMobile ? () => nav(`/post/${post.id}`) : undefined} data-testid={`post-bg-${post.id}`}>
          <AutoFitText text={displayContent} scale={post.text_scale || 1} maxPx={BG_TEXT_MAX_PX} boxClassName={BG_TEXT_PAD} className="font-bold text-white" style={BG_TEXT_SHADOW} testId={`post-bg-text-${post.id}`} />
        </BgTextFrame>
      ) : (
        displayContent && <ExpandableText as="div" text={displayContent} maxChars={feedTextMaxChars(config)} render={(s) => renderRichText(s, (leaf) => renderMentions(leaf, mentions))} className="mt-3 text-[15px] leading-relaxed break-words" testId={`post-text-${post.id}`} />
      )}

      {canTranslate && (
        <button
          onClick={doTranslate}
          disabled={translating}
          className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-semibold disabled:opacity-60"
          style={{ color: "var(--yalola-primary)" }}
          data-testid={`post-translate-${post.id}`}
        >
          <Languages size={13} />
          {translating ? t("post.translating", "Traduction…") : showTranslated ? t("post.see_original", "Voir l'original") : t("post.translate", "Traduire")}
        </button>
      )}

      {post.verse && (
        <div
          className="mt-3 rounded-2xl p-6 relative overflow-hidden"
          style={post.verse.bg && post.verse.bg.startsWith("/verse-bg/")
            ? { backgroundImage: `url(${post.verse.bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : { background: "var(--gradient-navy)" }}
          data-testid={`post-verse-${post.id}`}
        >
          {post.verse.bg && post.verse.bg.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/35" aria-hidden />}
          <div className="absolute -top-8 -right-8 opacity-[0.12]" aria-hidden>
            <Sparkles size={110} className="text-white" />
          </div>
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-accent)" }}>{t("verse.title", "Verset du jour")}</div>
            <p className="serif-italic mt-2 text-lg leading-relaxed text-white/95" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>« {post.verse[`text_${lang}`] || post.verse.text_fr || post.verse.text} »</p>
            <div className="mt-3 text-sm font-semibold" style={{ color: "var(--yalola-accent)" }}>— {localizeReference(post.verse.reference, lang)}</div>
          </div>
        </div>
      )}

      <DoubleTapHeart onDoubleTap={quickLove} testId={`post-dtap-${post.id}`}>
      {(() => {
        if (hideMedia) return null;
        const openAt = (i) => nav(`/post/${post.id}?photo=${i}`);
        // « Avant / Après » (2 photos → curseur ; 2 vidéos → côte à côte) puis mosaïque des albums avec vidéos
        // (2 côte à côte, 3 = 1 grande + 2, 4 = 2×2, 5+ = « +N ») — plus jamais une liste empilée.
        const ba = postBeforeAfter(post);
        if (ba) return <BeforeAfterBlock post={post} ba={ba} onOpen={openAt} />;
        if (postHasVideoAlbum(post)) return <AlbumMosaic post={post} items={post.album} onOpen={openAt} />;
        return null;
      })()}

      {!hideMedia && !postHasVideoAlbum(post) && !postBeforeAfter(post) && post.images?.length > 0 && (
        post.music_url ? (
          <div className="relative" ref={imgMusicRef} data-testid={`post-image-music-${post.id}`}>
            <PostPhotos images={post.images} dims={post.image_dims} onOpen={(i) => nav(`/post/${post.id}?photo=${i}`)} />
            <PostImageMusic containerRef={imgMusicRef} url={post.music_url} title={post.music_title} volume={typeof post.music_volume === "number" ? post.music_volume : 1} start={post.music_start || 0} testId={`post-image-music-ov-${post.id}`} />
          </div>
        ) : (
          <PostPhotos images={post.images} dims={post.image_dims} onOpen={(i) => nav(`/post/${post.id}?photo=${i}`)} />
        )
      )}
      {!hideMedia && !postHasVideoAlbum(post) && post.video_url && (
        post.is_reel
          ? <ReelFeedPreview post={post} />
          : <FeedVideo src={post.hls_url || post.video_url} poster={post.poster_url || undefined} width={post.video_width || 0} height={post.video_height || 0} isReplay={post.is_replay} duration={post.replay_duration || post.video_duration || null} initialProcessing={post.video_processing} hdProcessing={!!post.hls_transcoding && !post.hls_url} compact={promoted} adId={post.ad?.id || null} testId={`post-video-${post.id}`} musicUrl={post.music_url || null} musicTitle={post.music_title || null} keepMediaSound={!!post.music_keep_media_sound} musicVolume={typeof post.music_volume === "number" ? post.music_volume : 1} musicStart={post.music_start || 0} hasAudio={post.has_audio} resumeKey={post.id} resumeAt={post.resume_t || 0} fx={post.video_fx || null} onExpand={() => nav(`/post/${post.id}`)} />
      )}
      </DoubleTapHeart>
      {!hideMedia && !post.images?.length && !post.video_url && (() => {
        if (!embed) return null;
        if (embed.kind === "article") {
          return (
            <div className="mt-3 rounded-2xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`post-article-${post.id}`}>
              {embed.thumbnail && (
                <button type="button" onClick={() => nav(`/post/${post.id}`)} className="block w-full group overflow-hidden" data-testid={`post-article-image-${post.id}`}>
                  <img src={resolveMediaUrl(embed.thumbnail)} loading="lazy" decoding="async" alt="" className="w-full max-h-[420px] object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                </button>
              )}
              <a href={embed.url} target="_blank" rel="noreferrer" className="block px-4 py-3 transition-opacity hover:opacity-90" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`post-article-link-${post.id}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{embed.domain}</div>
                {embed.title && <div className="text-sm font-bold leading-snug" data-testid={`post-article-title-${post.id}`}>{embed.title}</div>}
                {embed.description && <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{embed.description}</div>}
              </a>
            </div>
          );
        }
        if (post.embed?.live_id) {
          return <EmbedPlayer embed={embed} testId={`post-embed-${post.id}`} preview={false} />;
        }
        return (
          <div>
            <div className="relative" data-testid={`post-embed-wrap-${post.id}`}>
              <EmbedPlayer embed={embed} testId={`post-embed-${post.id}`} />
              {/* Facebook-style: clicking the video opens the theater (sound + comments,
                  no redirect); the muted inline iframe stays as a live preview. */}
              <button
                type="button"
                onClick={() => nav(`/post/${post.id}`)}
                className="absolute inset-0 z-10"
                aria-label={t("post.open_video", "Ouvrir la vidéo")}
                data-testid={`post-embed-open-${post.id}`}
              />
            </div>
            {(post.embed?.title || post.embed?.author) && (
              <button type="button" onClick={() => nav(`/post/${post.id}`)} className="mt-1 w-full text-left rounded-xl border px-3 py-2 transition-opacity hover:opacity-90" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`post-embed-meta-${post.id}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
                  {embed.provider === "youtube" ? "YOUTUBE.COM" : "FACEBOOK.COM"}
                </div>
                {post.embed.title && <div className="text-sm font-semibold truncate">{post.embed.title}</div>}
                {post.embed.author && <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{post.embed.author}</div>}
              </button>
            )}
          </div>
        );
      })()}
      {post.audio_url && (
        <div className="mt-3" data-testid={`post-audio-${post.id}`}>
          <PostAudioPlayer post={post} author={author} testId={`post-audio-player-${post.id}`} />
        </div>
      )}

      {post.shared_post && (
        <div className="mt-3 rounded-2xl border p-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`post-shared-${post.id}`}>
          <SharedPostCard sp={post.shared_post} testId={`post-shared-card-${post.id}`} />
        </div>
      )}

      {post.collection_preview && (
        <Link to={`/collections/${post.collection_preview.id}`} className="mt-3 block rounded-2xl overflow-hidden border transition-shadow hover:shadow-md" style={{ borderColor: "var(--yalola-border)" }} data-testid={`post-collection-${post.id}`}>
          {post.collection_preview.thumbs?.length > 0 && (
            <div className={`grid gap-0.5 ${post.collection_preview.thumbs.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
              {post.collection_preview.thumbs.slice(0, 4).map((u, i) => (
                <img key={i} src={resolveMediaUrl(u)} loading="lazy" decoding="async" alt="" className="h-36 w-full object-cover" />
              ))}
            </div>
          )}
          <div className="p-3 flex items-center gap-2" style={{ background: "var(--yalola-surface-alt)" }}>
            <Sparkles size={16} style={{ color: "var(--yalola-primary)" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{post.collection_preview.name}</div>
              <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("collection.count", "Collection · {count} publication(s)").replace("{count}", post.collection_preview.count)}</div>
            </div>
            <span className="text-xs font-medium shrink-0" style={{ color: "var(--yalola-primary)" }}>{t("common.view", "Voir")} →</span>
          </div>
        </Link>
      )}

      {showPromoteNudge && (
        <div
          className="mt-3 w-full flex items-center gap-3 rounded-2xl border p-3 text-left"
          style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }}
          data-testid={`promote-nudge-${post.id}`}
        >
          <TrendingUp size={20} style={{ color: "var(--yalola-primary)" }} className="shrink-0" />
          <button onClick={() => setPromoteOpen(true)} className="flex-1 min-w-0 text-left" data-testid={`promote-nudge-cta-${post.id}`}>
            <div className="text-sm font-bold" style={{ color: "var(--yalola-primary)" }}>{t("post.nudge_title", "Cette publication plaît 🔥")}</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("post.nudge_body", "Touchez encore plus de personnes en la promouvant.")}</div>
          </button>
          <button onClick={() => setPromoteOpen(true)} className="pill pill-primary shrink-0" data-testid={`promote-nudge-btn-${post.id}`}>{t("post.promote", "Promouvoir")}</button>
          <button
            onClick={dismissNudge}
            className="shrink-0 h-7 w-7 grid place-items-center rounded-full transition-colors hover:bg-black/10"
            style={{ color: "var(--yalola-text-muted)" }}
            aria-label={t("post.nudge_dismiss", "Plus tard")}
            title={t("post.nudge_dismiss", "Plus tard")}
            data-testid={`promote-nudge-dismiss-${post.id}`}
          >
            <X size={15} />
          </button>
        </div>
      )}

      {post.ad && <AdCtaFooter ad={post.ad} author={post.author} />}

      <div ref={reactionAreaRef} className={pulseReactions ? "yalola-attn-pulse" : ""}>
        <ReactionBar post={post} onChange={onReactionChange} commentCount={commentCount} onCommentClick={handleCommentClick} autoOpenReactors={focusTarget === "reactions"} quickReactTick={quickReactTick} />
      </div>

      {showComments && (
        <div ref={commentsRef} className="mt-2 border-t pt-1" style={{ borderColor: "var(--yalola-border)" }} data-testid={`comments-${post.id}`}>
          <CommentThread
            post={post}
            variant="inline"
            focusCommentId={focusCommentId}
            stickyComposer={theaterMode}
          />
        </div>
      )}

      <PromoteModal postId={post.id} open={promoteOpen} onOpenChange={setPromoteOpen} onPromoted={() => setPromoted(true)} />
      <CommentModal post={shownPost} open={commentModalOpen} onOpenChange={onCommentModalChange} videoNav={videoNav} />
      <SaveCollectionDialog postId={post.id} open={collectionOpen} onOpenChange={setCollectionOpen} />
      {post.post_type === "testimony" && <ShareTestimony post={post} open={shareOpen} onOpenChange={setShareOpen} />}
      {post.audio_url && post.post_type !== "testimony" && <AudioSharePoster post={post} open={audioShareOpen} onOpenChange={setAudioShareOpen} t={t} />}
      {audienceEditable && (
        <Dialog open={audienceOpen} onOpenChange={setAudienceOpen}>
          <DialogContent variant="sheet" className="sm:max-w-md" data-testid={`post-audience-dialog-${post.id}`}>
            <DialogHeader>
              <DialogTitle>{t("audience.title", "Qui peut voir ta publication ?")}</DialogTitle>
              <DialogDescription>{t("audience.edit_hint", "Le changement s'applique tout de suite, pour tous.")}</DialogDescription>
            </DialogHeader>
            <DialogBody><AudienceOptions value={audience} onPick={changeAudience} t={t} tone="light" busy={audienceBusy} /></DialogBody>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid={`post-delete-confirm-${post.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{(post.is_replay || post.source_live_id) ? t("post.delete_replay_title", "Supprimer cette rediffusion ?") : t("post.delete_title", "Supprimer cette publication ?")}</AlertDialogTitle>
            <AlertDialogDescription>{(post.is_replay || post.source_live_id) ? t("post.delete_replay_desc_trash", "Le direct et sa rediffusion disparaîtront du fil, du profil et de « Mes directs ». Ils iront dans la corbeille de « Mes directs », récupérables 7 jours avant l'effacement définitif.") : t("post.delete_desc", "Cette action est définitive. La publication, ses réactions et commentaires seront supprimés.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`post-delete-no-${post.id}`}>{t("common.cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid={`post-delete-yes-${post.id}`}>{t("common.delete", "Supprimer")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BlockUserDialog open={blockOpen} onOpenChange={setBlockOpen} user={author} onBlocked={() => onHidden?.(post.id)} testidPrefix={`post-block-${post.id}`} />

      {isReplayPost && isOwner && (
        <AttachLiveToChurchDialog
          open={attachOpen}
          onOpenChange={setAttachOpen}
          liveId={post.source_live_id}
          postId={post.id}
          currentChurchId={attachedChurchId}
          onAttached={(c) => setAttachedChurchId(c.id)}
        />
      )}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md" data-testid={`post-report-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle>{t("report.title", "Signaler cette publication")}</DialogTitle>
            <DialogDescription>{t("report.subtitle", "Choisis un motif. Notre équipe de modération examinera ce signalement.")}</DialogDescription>
          </DialogHeader>

          <button
            type="button"
            disabled={reporting}
            onClick={() => doReport(t("report.reason_child", "Mise en danger d'un mineur / sécurité des enfants"), "child_safety")}
            className="w-full flex items-start gap-3 rounded-xl border-2 p-3 text-left transition-colors hover:bg-red-50 disabled:opacity-50"
            style={{ borderColor: "#DC2626", display: config?.settings?.csae?.report_option_enabled === false ? "none" : undefined }}
            data-testid={`post-report-child-safety-${post.id}`}
          >
            <ShieldAlert size={20} className="shrink-0 mt-0.5 text-red-600" />
            <span>
              <span className="block text-sm font-semibold text-red-700">{t("report.reason_child", "Mise en danger d'un mineur / sécurité des enfants")}</span>
              <span className="block text-xs text-red-600/80">{t("report.reason_child_hint", "Contenu impliquant l'exploitation ou la mise en danger d'un enfant. Traité en priorité absolue.")}</span>
            </span>
          </button>

          <div className="space-y-1.5">
            {REPORT_REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                disabled={reporting}
                onClick={() => doReport(r.label, r.key)}
                className="w-full flex items-center gap-2 rounded-xl border p-2.5 text-left text-sm transition-colors hover:bg-[var(--yalola-surface-alt)] disabled:opacity-50"
                style={{ borderColor: "var(--yalola-border)" }}
                data-testid={`post-report-reason-${r.key}-${post.id}`}
              >
                <Flag size={15} className="shrink-0" style={{ color: "var(--yalola-text-muted)" }} />
                {r.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
};
